import { InventoryBatch } from '../types/operations';
import { equipmentTracking, TrackingCategory, DateTrackingSettings } from './categoryTracking';

export interface DateSignals {
  unknown: boolean;
  warning: boolean;
  critical: boolean;
}
export const emptyDateSignals = (): DateSignals => ({ unknown: false, warning: false, critical: false });

export function inventoryDay(now = new Date()): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Europe/Warsaw', year: 'numeric', month: '2-digit', day: '2-digit',
  }).format(now);
}

function monthsAfter(day: string, months: number): string {
  const [year, month, date] = day.split('-').map(Number);
  const last = new Date(Date.UTC(year, month - 1 + months + 1, 0));
  return new Date(Date.UTC(last.getUTCFullYear(), last.getUTCMonth(), Math.min(date, last.getUTCDate())))
    .toISOString().slice(0, 10);
}

/** Only enabled dates on positive quantities signal; calendar thresholds include their last day. */
export function inventoryDateSignals(
  batches: readonly InventoryBatch[], quantity: number, tracking: DateTrackingSettings, today = inventoryDay(),
): DateSignals {
  const result = emptyDateSignals();
  if (quantity <= 0 || !(tracking.tracksExpiry || tracking.tracksReview)) return result;
  const month = monthsAfter(today, 1);
  const quarter = monthsAfter(today, 3);
  let recorded = 0;
  for (const batch of batches) {
    if (batch.quantity <= 0) continue;
    recorded += batch.quantity;
    const dates = [
      ...(tracking.tracksExpiry ? [batch.expiresOn] : []),
      ...(tracking.tracksReview ? [batch.reviewOn] : []),
    ];
    for (const date of dates) {
      if (!date || !/^\d{4}-\d{2}-\d{2}$/.test(date) || !Number.isFinite(Date.parse(date)) ||
          new Date(date).toISOString().slice(0, 10) !== date) result.unknown = true;
      else if (date <= month) result.critical = true;
      else if (date <= quarter) result.warning = true;
    }
  }
  // Old or incomplete batch data can leave part of the aggregate unassigned.
  if (recorded < quantity) result.unknown = true;
  return result;
}

export function buildInventoryDateSignals(
  categories: readonly TrackingCategory[],
  equipment: readonly (DateTrackingSettings & { id: string; categoryId?: string })[],
  positions: readonly { equipmentId: string; quantity: number }[],
  batches: readonly InventoryBatch[],
  today = inventoryDay(),
) {
  const categoryMap = new Map(categories.map(category => [category.id, category]));
  const equipmentMap = new Map(equipment.map(item => [item.id, item]));
  const batchesByEquipment = new Map<string, InventoryBatch[]>();
  for (const batch of batches) {
    const list = batchesByEquipment.get(batch.equipmentId) ?? [];
    list.push(batch);
    batchesByEquipment.set(batch.equipmentId, list);
  }
  const products = new Map<string, DateSignals>();
  const groups = new Map<string, DateSignals>();
  for (const position of positions) {
    const item = equipmentMap.get(position.equipmentId);
    if (!item) continue;
    const signals = inventoryDateSignals(batchesByEquipment.get(item.id) ?? [], position.quantity,
      equipmentTracking(item, categoryMap), today);
    products.set(item.id, signals);
    let categoryId = item.categoryId;
    const seen = new Set<string>();
    while (categoryId && !seen.has(categoryId)) {
      seen.add(categoryId);
      const parent = groups.get(categoryId) ?? emptyDateSignals();
      parent.unknown ||= signals.unknown;
      parent.warning ||= signals.warning;
      parent.critical ||= signals.critical;
      groups.set(categoryId, parent);
      categoryId = categoryMap.get(categoryId)?.parentId ?? undefined;
    }
  }
  return { products, categories: groups };
}
